<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useCartStore } from '@/stores'

const { t } = useI18n()
const route = useRoute()
const cartStore = useCartStore()

const itemCount = computed(() => cartStore.cartItemCount)
const hasItems = computed(() => itemCount.value > 0)

const isOnCartPage = computed(() =>
  route.path === '/cart' ||
  route.path.includes('/cart/') ||
  route.name === 'dashboardCart'
)

const isInDashboard = computed(() => route.path.startsWith('/dashboard'))

const showButton = computed(() => isInDashboard.value && hasItems.value && !isOnCartPage.value)
</script>

<template>
  <Teleport to="body">
    <Transition name="cart-fab">
      <router-link
        v-if="showButton"
        :to="{ name: 'dashboardCart' }"
        class="floating-cart-btn"
        :aria-label="t('a11y.goToCart')"
      >
        <v-icon icon="mdi-cart" size="24" />
        <span class="cart-count">{{ itemCount }}</span>
      </router-link>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.floating-cart-btn {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #00B0FF, #1FE2D4);
  border-radius: 16px;
  box-shadow:
    0 4px 20px rgba(0, 176, 255, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  color: white;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow:
      0 6px 28px rgba(0, 176, 255, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.15) inset;
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
}

.cart-count {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #FF5252;
  border-radius: 11px;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(255, 82, 82, 0.4);
}

/* Transition */
.cart-fab-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.cart-fab-leave-active {
  transition: all 0.2s ease;
}

.cart-fab-enter-from {
  opacity: 0;
  transform: scale(0.5) translateY(20px);
}

.cart-fab-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Responsive */
@media (max-width: 600px) {
  .floating-cart-btn {
    bottom: 16px;
    right: 16px;
    width: 52px;
    height: 52px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .floating-cart-btn {
    transition: none;

    &:hover {
      transform: none;
    }
  }

  .cart-fab-enter-active,
  .cart-fab-leave-active {
    transition: opacity 0.15s ease;
  }

  .cart-fab-enter-from,
  .cart-fab-leave-to {
    transform: none;
  }
}
</style>
